import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, Minus, AlertTriangle, Lightbulb, 
  RefreshCw, Globe, Package, Shield, Loader2,
  MapPin, Clock, Target, BarChart3, FileText, Flag, CheckCircle,
  ExternalLink, ChevronDown, ChevronUp, Newspaper, Link2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function MarketTrends() {
  const navigate = useNavigate();
  const [trends, setTrends] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [expandedAlerts, setExpandedAlerts] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [showAllNews, setShowAllNews] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  
  // Toggle expanded state for alerts
  const toggleAlertExpand = (index) => {
    setExpandedAlerts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  // Toggle expanded state for products
  const toggleProductExpand = (index) => {
    setExpandedProducts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  useEffect(() => {
    if (user?.role !== 'seller') {
      toast.error('Only sellers can access market trends');
      navigate('/dashboard');
      return;
    }
    fetchTrends(false); // Load cached data on mount
  }, []);

  const fetchTrends = async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai/market-trends`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { refresh: forceRefresh }
      });
      setTrends(response.data);
      setLastRefresh(new Date());
      if (forceRefresh) {
        toast.success('Market data refreshed successfully!');
      }
    } catch (error) {
      toast.error('Failed to fetch market trends');
    } finally {
      setIsLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    const t = trend?.toLowerCase();
    if (t === 'rising' || t === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (t === 'declining' || t === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-slate-400" />;
  };

  const getTrendColor = (trend) => {
    const t = trend?.toLowerCase();
    if (t === 'rising' || t === 'up') return 'text-green-600';
    if (t === 'declining' || t === 'down') return 'text-red-600';
    return 'text-slate-600';
  };

  const getImpactColor = (impact) => {
    const i = impact?.toLowerCase();
    if (i === 'high') return 'bg-red-100 text-red-800 border-red-200';
    if (i === 'medium') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase();
    if (p === 'high') return 'bg-red-500';
    if (p === 'medium') return 'bg-amber-500';
    return 'bg-green-500';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="market-trends-loading">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground font-medium">Generating Market Intelligence Report...</p>
          <p className="text-sm text-muted-foreground mt-1">Analyzing trade data and market signals</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="market-trends-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary mb-1">
              Market Intelligence Report
            </h1>
            <p className="text-muted-foreground">
              Data-driven insights for exporters worldwide
            </p>
            {trends?.data_credibility && (
              <div className="flex items-center gap-2 mt-2">
                <Badge 
                  variant={trends.data_credibility.data_type === 'LIVE' ? 'default' : 'secondary'}
                  className={trends.data_credibility.data_type === 'LIVE' ? 'bg-green-600' : trends.data_credibility.data_type === 'FALLBACK' ? 'bg-amber-500' : ''}
                >
                  {trends.data_credibility.data_type === 'LIVE' ? '● Live' : 
                   trends.data_credibility.data_type === 'CACHED' ? '● Cached' : '● Fallback'}
                </Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Updated {trends.data_credibility.last_updated_display || trends.data_credibility.last_updated}
                </span>
              </div>
            )}
          </div>
          <Button onClick={() => fetchTrends(true)} disabled={isLoading} data-testid="refresh-trends-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>

        {/* What Changed Today Card */}
        {trends?.whats_changed && (
          <Card className="mb-6 border-l-4 border-l-amber-500" data-testid="whats-changed-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2 text-lg">
                <Newspaper className="h-5 w-5 text-amber-500" />
What&apos;s New Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trends.whats_changed.has_new_developments ? (
                <>
                  <div className="space-y-3 mb-4">
                    {trends.whats_changed.whats_new_today?.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Badge variant="outline" className="mt-0.5 shrink-0">{item.sector}</Badge>
                        <div>
                          <p className="font-medium text-sm">{item.headline}</p>
                          {item.detail && <p className="text-sm text-muted-foreground">{item.detail}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {trends.whats_changed.why_it_matters && (
                    <div className="bg-amber-50 rounded-lg p-3 mb-2">
                      <p className="text-sm font-medium mb-1">Why it matters</p>
                      <p className="text-sm text-muted-foreground">{trends.whats_changed.why_it_matters}</p>
                    </div>
                  )}
                </>
              ) : null}
              <p className="text-sm text-muted-foreground italic">
                What changed since the previous report: {trends.whats_changed.summary}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Market Overview Card */}
        {trends?.market_overview && (
          <Card className="mb-6 border-l-4 border-l-primary" data-testid="market-overview-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-primary" />
                Market Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground mb-3">{trends.market_overview.summary}</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Growth Rate:</span>
                  <Badge className={trends.market_overview.growth_rate?.startsWith('+') ? 'bg-green-600' : 'bg-red-600'}>
                    {trends.market_overview.growth_rate}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Key Regions:</span>
                  <span className="font-medium">{trends.market_overview.key_regions?.join(', ')}</span>
                </div>
              </div>
              {trends.market_overview.primary_driver && (
                <p className="text-sm text-muted-foreground mt-2">
                  <span className="font-medium">Primary Driver:</span> {trends.market_overview.primary_driver}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Trending Products */}
          <Card data-testid="trending-products-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Trending Products
              </CardTitle>
              <CardDescription>Products with increasing global demand - based on real news analysis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {trends?.trending_products?.map((product, index) => (
                <div 
                  key={index} 
                  className={`p-4 bg-slate-50 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    expandedProducts[index] ? 'border-green-300 bg-green-50/50' : ''
                  }`}
                  onClick={() => toggleProductExpand(index)}
                  data-testid={`trending-product-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold">{product.product}</h4>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge variant="outline">{product.industry}</Badge>
                        {product.hs_code && (
                          <Badge variant="secondary" className="font-mono text-xs">
                            HS: {product.hs_code}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        {getTrendIcon(product.demand_trend)}
                        <span className={`text-sm font-medium ${getTrendColor(product.demand_trend)}`}>
                          {product.demand_change_percent}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs text-muted-foreground">Confidence:</span>
                        <Badge variant="secondary" className="text-xs">
                          {product.confidence_score}%
                        </Badge>
                        {expandedProducts[index] ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground ml-1" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground ml-1" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* HS Code Description */}
                  {product.hs_description && (
                    <p className="text-xs text-muted-foreground bg-slate-100 px-2 py-1 rounded mb-2">
                      <span className="font-medium">HS Classification:</span> {product.hs_description}
                    </p>
                  )}
                  
                  <p className="text-sm text-muted-foreground mb-2">{product.reason}</p>
                  
                  {/* Expanded: Show news sources with links */}
                  {expandedProducts[index] && (product.news_links || product.news_sources) && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Newspaper className="h-3 w-3" />
                        News sources for this trend:
                      </p>
                      {/* Use news_links if available (has clickable links) */}
                      {product.news_links ? (
                        product.news_links.map((source, idx) => (
                          <a 
                            key={idx} 
                            href={source.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="block text-xs bg-white px-3 py-2 rounded border hover:border-primary/50 hover:bg-primary/5 transition-all"
                          >
                            <div className="flex items-start gap-2">
                              <ExternalLink className="h-3 w-3 mt-0.5 text-primary shrink-0" />
                              <span className="text-slate-700 hover:text-primary">{source.title}</span>
                            </div>
                          </a>
                        ))
                      ) : (
                        // Fallback to news_sources (titles only)
                        product.news_sources.map((source, idx) => (
                          <div key={idx} className="text-xs bg-white px-2 py-1.5 rounded border flex items-start gap-2">
                            <Link2 className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
                            <span className="text-muted-foreground">{source}</span>
                          </div>
                        ))
                      )}
                      <p className="text-xs text-green-700 flex items-center gap-1 mt-2">
                        <CheckCircle className="h-3 w-3" />
                        Trend verified from {(product.news_links || product.news_sources)?.length || 0} news articles
                      </p>
                    </div>
                  )}
                  
                  {/* Preview when collapsed */}
                  {!expandedProducts[index] && (product.news_links || product.news_sources) && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Newspaper className="h-3 w-3" />
                      Click to see {(product.news_links || product.news_sources)?.length || 0} news sources with links
                    </p>
                  )}
                  
                  {/* Price Range */}
                  {product.price_range_usd && (
                    <p className="text-sm font-medium text-green-700 mb-2">
                      Export Price: {product.price_range_usd}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-1 mt-2">
                    {product.key_markets?.map((market, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        <MapPin className="h-3 w-3 mr-1" />
                        {market}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Market Alerts */}
          <Card data-testid="market-alerts-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Market Alerts
              </CardTitle>
              <CardDescription>Critical updates from real news sources - click to verify</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {trends?.market_alerts?.map((alert, index) => (
                <div 
                  key={index} 
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    expandedAlerts[index] ? 'bg-slate-50 border-primary/30' : ''
                  }`}
                  onClick={() => toggleAlertExpand(index)}
                  data-testid={`market-alert-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-2 flex-1">
                      <h4 className="font-semibold text-sm leading-tight">{alert.title}</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getImpactColor(alert.impact_level || alert.impact)}>
                        {alert.impact_level || alert.impact} impact
                      </Badge>
                      {expandedAlerts[index] ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded content */}
                  {expandedAlerts[index] && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                      <p className="text-sm text-muted-foreground">{alert.explanation || alert.description}</p>
                      
                      {/* Source and Link */}
                      {alert.link && (
                        <a 
                          href={alert.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 text-sm text-primary hover:underline bg-primary/5 px-3 py-2 rounded-md"
                        >
                          <ExternalLink className="h-4 w-4" />
                          <span>Read full article from {alert.source || 'source'}</span>
                        </a>
                      )}
                      
                      {/* Affected regions */}
                      <div className="flex flex-wrap gap-1">
                        {(alert.affected_regions || alert.affected_categories)?.map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item}
                          </Badge>
                        ))}
                      </div>
                      
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Data sourced from verified news outlets
                      </p>
                    </div>
                  )}
                  
                  {/* Preview when collapsed */}
                  {!expandedAlerts[index] && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Click to see source and full details
                    </p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Actionable Insights */}
          <Card data-testid="actionable-insights-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-500" />
                Actionable Insights
              </CardTitle>
              <CardDescription>Specific steps you can take now</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {trends?.actionable_insights?.map((insight, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                  data-testid={`insight-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${getPriorityColor(insight.priority)}`} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900">{insight.action}</h4>
                      <p className="text-sm text-blue-800 mt-1">{insight.why}</p>
                      <Badge variant="outline" className="mt-2 text-xs">
                        {insight.priority} Priority
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
              {/* Fallback for old format */}
              {!trends?.actionable_insights && trends?.opportunities?.map((opp, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                  data-testid={`opportunity-${index}`}
                >
                  <h4 className="font-semibold text-blue-900">{opp.title}</h4>
                  <p className="text-sm text-blue-800 mt-1">{opp.description}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {opp.potential_markets?.map((market, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        <Globe className="h-3 w-3 mr-1" />
                        {market}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* India Specific Insights */}
          {trends?.india_insights && (
            <Card data-testid="india-insights-card" className="border-l-4 border-l-orange-500">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Flag className="h-5 w-5 text-orange-500" />
                  India-Specific Insights
                </CardTitle>
                <CardDescription>What this means for exporters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Target Markets */}
                <div>
                  <h5 className="text-sm font-medium text-muted-foreground mb-2">Target Export Markets</h5>
                  <div className="flex flex-wrap gap-2">
                    {trends.india_insights.target_markets?.map((market, idx) => (
                      <Badge key={idx} className="bg-orange-100 text-orange-800 border-orange-200">
                        <Globe className="h-3 w-3 mr-1" />
                        {market}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Positioning */}
                {trends.india_insights.positioning && (
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <h5 className="text-sm font-medium text-orange-900 mb-1">Product Positioning</h5>
                    <p className="text-sm text-orange-800">{trends.india_insights.positioning}</p>
                  </div>
                )}

                {/* Opportunities */}
                {trends.india_insights.opportunities?.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-muted-foreground mb-2">Key Opportunities</h5>
                    <ul className="space-y-1">
                      {trends.india_insights.opportunities.map((opp, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{opp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Competitive Advantage */}
                {trends.india_insights.advantage && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h5 className="text-sm font-medium text-green-900 mb-1">India's Competitive Advantage</h5>
                    <p className="text-sm text-green-800">{trends.india_insights.advantage}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Raw News Sources - Full Transparency */}
        {trends?.news_articles && trends.news_articles.length > 0 && (
          <Card className="mt-6" data-testid="news-sources-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Newspaper className="h-5 w-5 text-slate-600" />
                    News Sources
                  </CardTitle>
                  <CardDescription>
                    All {trends.news_articles.length} articles used to generate these insights - click to verify
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAllNews(!showAllNews)}
                >
                  {showAllNews ? 'Show Less' : `Show All ${trends.news_articles.length}`}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(showAllNews ? trends.news_articles : trends.news_articles.slice(0, 5)).map((article, index) => (
                  <a
                    key={index}
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 border rounded-lg hover:bg-slate-50 hover:border-primary/30 transition-all group"
                    data-testid={`news-article-${index}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm group-hover:text-primary transition-colors line-clamp-2">
                          {article.title}
                        </h4>
                        {article.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {article.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {article.source}
                          </Badge>
                          {article.published && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(article.published).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" />
                    </div>
                  </a>
                ))}
              </div>
              
              {!showAllNews && trends.news_articles.length > 5 && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  + {trends.news_articles.length - 5} more articles
                </p>
              )}
              
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-800 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>
                    <strong>Data Transparency:</strong> All market insights are derived from these real news sources. 
                    Click any article to verify the information at its original source.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Credibility Footer */}
        {trends?.data_credibility && (
          <Card className="mt-6 bg-slate-50" data-testid="data-credibility-card">
            <CardContent className="py-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>{trends.data_credibility.basis}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={trends.data_credibility.data_type === 'LIVE' ? 'border-green-500 text-green-700' : 
                                 trends.data_credibility.data_type === 'CACHED' ? 'border-blue-500 text-blue-700' : 
                                 'border-amber-500 text-amber-700'}
                    >
                      {trends.data_credibility.data_type}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-muted-foreground border-t pt-3">
                  <span><strong>Sources:</strong> {trends.data_credibility.sources}</span>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Last Updated: {trends.data_credibility.last_updated_display || trends.data_credibility.last_updated}
                    </span>
                    {trends.data_credibility.cache_ttl_hours && (
                      <span className="text-blue-600">
                        Cache: {trends.data_credibility.cache_ttl_hours}h TTL
                      </span>
                    )}
                  </div>
                </div>
                {trends.data_credibility.note && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-md">
                    {trends.data_credibility.note}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fallback notice */}
        {trends?.fallback && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            Showing cached market data. Live analysis unavailable.
          </p>
        )}
      </div>
    </Layout>
  );
}
